import { useState } from "react";

import { api, type AgentRuntime } from "../lib/api.ts";

/**
 * Per-agent runtime tunables editor. Same edit-toggle pattern as
 * AgentClaudeSettingsEditor. Saves via PUT /api/agents/:key/runtime which
 * opens a single-file PR against agents-extra.json. Running agents pick up
 * new values on restart (CLI fetches runtime-config at startup); a banner
 * makes this explicit.
 *
 * Fields exposed (v1):
 *   - taskTimeoutMs: per `claude -p` subprocess wall-clock budget. UI shows
 *     this as minutes for readability. 30s..30min range enforced server-side.
 *   - agentMaxCostUsd: advisory per-task USD cap. Used as the default for
 *     followup-creation; agent runtime doesn't kill subprocess on overage.
 */
export function AgentRuntimeEditor({
	agentKey,
	initialRuntime,
	disabled,
}: {
	agentKey: string;
	initialRuntime: AgentRuntime | null | undefined;
	disabled?: boolean;
}) {
	const [editing, setEditing] = useState(false);
	const [current, setCurrent] = useState<AgentRuntime | null>(initialRuntime ?? null);
	const [draftTimeoutMin, setDraftTimeoutMin] = useState<string>(
		initialRuntime?.taskTimeoutMs ? String(Math.round(initialRuntime.taskTimeoutMs / 60_000)) : "",
	);
	const [draftMaxCost, setDraftMaxCost] = useState<string>(
		initialRuntime?.agentMaxCostUsd != null ? String(initialRuntime.agentMaxCostUsd) : "",
	);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [lastResult, setLastResult] = useState<string | null>(null);

	function startEdit() {
		setDraftTimeoutMin(
			current?.taskTimeoutMs ? String(Math.round(current.taskTimeoutMs / 60_000)) : "",
		);
		setDraftMaxCost(current?.agentMaxCostUsd != null ? String(current.agentMaxCostUsd) : "");
		setError(null);
		setLastResult(null);
		setEditing(true);
	}

	async function handleSave(clear?: boolean) {
		setBusy(true);
		setError(null);
		setLastResult(null);
		try {
			let payload: AgentRuntime | null;
			if (clear) {
				payload = null;
			} else {
				payload = {};
				const tm = draftTimeoutMin.trim();
				if (tm) {
					const n = Number.parseFloat(tm);
					if (Number.isNaN(n) || n <= 0) throw new Error("Timeout (minutes) must be a positive number.");
					if (n < 0.5) throw new Error("Timeout floor is 0.5 minutes (30s).");
					if (n > 30) throw new Error("Timeout ceiling is 30 minutes.");
					payload.taskTimeoutMs = Math.round(n * 60_000);
				}
				const mc = draftMaxCost.trim();
				if (mc) {
					const n = Number.parseFloat(mc);
					if (Number.isNaN(n) || n <= 0) throw new Error("Max cost must be a positive number (USD).");
					if (n > 30) throw new Error("Max cost ceiling is $30.");
					payload.agentMaxCostUsd = n;
				}
				if (!payload.taskTimeoutMs && !payload.agentMaxCostUsd) {
					throw new Error("Set at least one field, or use Clear.");
				}
			}
			const res = await api.setAgentRuntime(agentKey, payload);
			setCurrent(payload);
			setEditing(false);
			if ("noop" in res && res.noop) {
				setLastResult("No change.");
			} else if ("htmlUrl" in res) {
				setLastResult(`PR opened: ${res.htmlUrl}${res.merged ? " (auto-merged)" : ""}`);
			}
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	const timeoutMin = current?.taskTimeoutMs ? current.taskTimeoutMs / 60_000 : null;

	return (
		<section className="mt-6 border border-zinc-800 rounded-lg bg-zinc-900/30 p-4">
			<div className="flex items-baseline justify-between mb-2">
				<div>
					<h2 className="text-base font-semibold">Runtime tunables</h2>
					<div className="text-xs text-zinc-500 mt-0.5">
						Per-agent <code className="font-mono">claude -p</code> wall-clock budget and per-task cost cap. CLI uses these as defaults; env vars (
						<code className="font-mono">G1_AGENT_TASK_TIMEOUT_MS</code>) still override at launch.
					</div>
				</div>
				{!editing && !disabled && (
					<button
						type="button"
						onClick={startEdit}
						className="text-xs px-2 py-1 rounded border border-zinc-700 hover:border-zinc-500 text-zinc-300"
					>
						Edit
					</button>
				)}
			</div>

			{!editing ? (
				<div className="text-sm text-zinc-400 grid grid-cols-2 gap-3 mt-2">
					<div>
						<div className="text-xs text-zinc-500 uppercase tracking-wide">claude -p timeout</div>
						<div className="font-mono text-xs mt-0.5">
							{timeoutMin != null ? `${timeoutMin} min (${current!.taskTimeoutMs} ms)` : "(default: 5 min)"}
						</div>
					</div>
					<div>
						<div className="text-xs text-zinc-500 uppercase tracking-wide">per-task max cost (USD)</div>
						<div className="font-mono text-xs mt-0.5">
							{current?.agentMaxCostUsd != null ? `$${current.agentMaxCostUsd.toFixed(2)}` : "(no cap)"}
						</div>
					</div>
				</div>
			) : (
				<div className="space-y-3 mt-2">
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						<label className="block">
							<span className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">
								claude -p timeout (minutes, 0.5 – 30)
							</span>
							<input
								type="number"
								step="0.5"
								min="0.5"
								max="30"
								placeholder="15"
								value={draftTimeoutMin}
								onChange={(e) => setDraftTimeoutMin(e.target.value)}
								className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm font-mono"
							/>
						</label>
						<label className="block">
							<span className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">
								per-task max cost (USD, 0 – 30)
							</span>
							<input
								type="number"
								step="0.5"
								min="0"
								max="30"
								placeholder="3"
								value={draftMaxCost}
								onChange={(e) => setDraftMaxCost(e.target.value)}
								className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm font-mono"
							/>
						</label>
					</div>

					{error && (
						<div className="p-2 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300">
							{error}
						</div>
					)}

					<div className="text-xs text-zinc-500 italic">
						Save opens a PR against <code className="font-mono">agents/&lt;key&gt;/agent.json</code>. After merge + deploy, restart the agent on its host to pick up new values.
					</div>

					<div className="flex gap-2">
						<button
							type="button"
							onClick={() => handleSave(false)}
							disabled={busy}
							className="px-3 py-1.5 text-sm rounded bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50"
						>
							{busy ? "Saving…" : "Save"}
						</button>
						<button
							type="button"
							onClick={() => handleSave(true)}
							disabled={busy}
							className="px-3 py-1.5 text-sm rounded border border-zinc-700 hover:border-zinc-500"
							title="Remove all per-agent overrides; CLI falls back to env vars / 5-min default"
						>
							Clear overrides
						</button>
						<button
							type="button"
							onClick={() => setEditing(false)}
							disabled={busy}
							className="px-3 py-1.5 text-sm rounded border border-zinc-800 hover:border-zinc-700"
						>
							Cancel
						</button>
					</div>
				</div>
			)}

			{lastResult && (
				<div className="mt-2 text-xs text-zinc-400">
					{lastResult.startsWith("PR opened:") ? (
						<>
							PR opened:{" "}
							<a
								href={lastResult.replace("PR opened: ", "").replace(" (auto-merged)", "")}
								target="_blank"
								rel="noopener noreferrer"
								className="text-zinc-200 underline-offset-2 hover:underline"
							>
								{lastResult.replace("PR opened: ", "").replace(" (auto-merged)", "")}
							</a>
							{lastResult.endsWith("(auto-merged)") && " (auto-merged)"}
						</>
					) : (
						lastResult
					)}
				</div>
			)}
		</section>
	);
}
